import { useEffect, useMemo, useRef, useState } from "react";
import { Play, Pause, Volume2, VolumeX, Loader2, Heart, Share2, Moon } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import { isFavorite, toggleFavorite } from "./Favorites";
import { generateShareCard } from "../lib/shareCard";
import { lookupCode } from "../lib/songMatch";
import SharePreviewModal from "./SharePreviewModal";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
// Direct HTTPS connection to Shoutcast = fewer network hops = faster start.
// Fallback to backend proxy only if direct fails (CORS / mixed content / network).
const DIRECT_STREAM_URL = "https://sc.cast-control.eu:8030/stream";
const PROXY_STREAM_URL  = `${API}/stream`;

function Reel({ playing, side }) {
  // Detailed Maxell-style reel hub with 6 finger holes around a central gear
  return (
    <div
      data-testid={`cassette-reel-${side}`}
      className="relative w-[88px] h-[88px] sm:w-[120px] sm:h-[120px] md:w-[140px] md:h-[140px] shrink-0 rounded-full bg-[radial-gradient(circle_at_center,#0a0907_0%,#000_70%)] shadow-[inset_0_3px_8px_rgba(0,0,0,0.8),0_2px_6px_rgba(0,0,0,0.4)]"
    >
      {/* Outer black tape spool ring */}
      <div className="absolute inset-[6%] rounded-full bg-[#050505] border border-[#1a1a1a]" />

      {/* Spinning hub */}
      <div
        className={`absolute inset-[18%] rounded-full bg-gradient-to-br from-[#e8e6df] to-[#a8a5a0] shadow-[inset_0_-3px_6px_rgba(0,0,0,0.35),0_0_0_2px_#0a0907] ${playing ? "reel-spin" : "reel-spin paused"}`}
      >
        {/* 6 finger holes for the cassette deck pins */}
        {Array.from({ length: 6 }).map((_, i) => (
          <div
            key={i}
            className="absolute left-1/2 top-1/2 w-[16%] h-[28%] -translate-x-1/2 -translate-y-1/2 origin-center"
            style={{ transform: `translate(-50%, -50%) rotate(${i * 60}deg) translateY(-50%)` }}
          >
            <div className="w-full h-full rounded-full bg-[#0a0907] shadow-[inset_0_2px_3px_rgba(0,0,0,0.8)]" />
          </div>
        ))}
        {/* Central gear teeth */}
        <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-[34%] h-[34%] rounded-full bg-[#0a0907] flex items-center justify-center">
          {Array.from({ length: 8 }).map((_, i) => (
            <span
              key={i}
              className="absolute w-[18%] h-[55%] bg-[#0a0907]"
              style={{ transform: `rotate(${i * 45}deg) translateY(-95%)` }}
            />
          ))}
          <span className="absolute w-[45%] h-[45%] rounded-full bg-[#1a1714] border border-[#3a3128]" />
        </div>
      </div>
    </div>
  );
}

function Equalizer({ active }) {
  const bars = useMemo(() => [0.2, 0.55, 0.35, 0.8, 0.45, 0.95, 0.6, 0.3, 0.75, 0.5, 0.4, 0.85], []);
  return (
    <div data-testid="equalizer" className="flex items-end gap-[3px] h-10">
      {bars.map((h, i) => (
        <span
          key={i}
          className={`w-1.5 rounded-sm bg-[#FF6B00] shadow-[0_0_6px_rgba(255,107,0,0.7)] ${active ? "eq-bar" : ""}`}
          style={{
            height: `${Math.max(15, h * 100)}%`,
            animationDelay: `${i * 80}ms`,
            animationPlayState: active ? "running" : "paused",
            opacity: active ? 1 : 0.25,
          }}
        />
      ))}
    </div>
  );
}

export default function CassettePlayer() {
  const audioRef = useRef(null);
  const sessionIdRef = useRef(
    (typeof crypto !== "undefined" && crypto.randomUUID && crypto.randomUUID()) ||
      `s_${Math.random().toString(36).slice(2)}`
  );

  const [isPlaying, setIsPlaying] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [volume, setVolume] = useState(0.7);
  const [muted, setMuted] = useState(false);
  const [nowPlaying, setNowPlaying] = useState({
    title: null,
    artist: null,
    is_online: false,
    listeners: null,
  });
  const [isFav, setIsFav] = useState(false);
  // Share preview modal state
  const [sharePreview, setSharePreview] = useState({ open: false, blob: null, text: "", url: "" });
  // Sleep timer state — absolute timestamp when the radio should auto-pause
  const [sleepUntil, setSleepUntil] = useState(null);
  const [sleepMenuOpen, setSleepMenuOpen] = useState(false);
  const [sleepRemaining, setSleepRemaining] = useState(0); // seconds

  // ==== Emit `rrz:now-playing` event whenever the track changes ====
  const lastEmittedRef = useRef("");
  const lastAutoplayedRef = useRef(""); // avoid re-triggering on same track
  const isPlayingRef = useRef(false);
  useEffect(() => { isPlayingRef.current = isPlaying; }, [isPlaying]);
  useEffect(() => {
    const raw = (nowPlaying.title || "").trim();
    if (!raw) return;
    if (raw.toLowerCase() === lastEmittedRef.current) return;
    lastEmittedRef.current = raw.toLowerCase();
    window.dispatchEvent(new CustomEvent("rrz:now-playing", {
      detail: {
        title: raw,
        listeners: nowPlaying.listeners ?? null,
        ts: Date.now(),
      },
    }));
    const fav = isFavorite(raw);
    setIsFav(fav);

    // Auto-play on favorite: user opted in via the toggle in Favorites header.
    // Only kick in if radio is idle AND this song hasn't been auto-played yet.
    if (fav && !isPlayingRef.current && lastAutoplayedRef.current !== raw.toLowerCase()) {
      let enabled = false;
      try { enabled = localStorage.getItem("rrz-autoplay-on-fav") === "1"; } catch {}
      if (enabled) {
        lastAutoplayedRef.current = raw.toLowerCase();
        toast.success(`♥ Sta passando "${raw}"`, {
          description: "Avvio automatico in corso…",
        });
        // Fire-and-forget: togglePlay handles its own errors (autoplay policy, etc.)
        setTimeout(() => { try { togglePlay(); } catch {} }, 50);
      }
    }
  }, [nowPlaying.title, nowPlaying.listeners]); // eslint-disable-line react-hooks/exhaustive-deps

  // Sync heart with global favorites store (in case user edits from another tab / list)
  useEffect(() => {
    const refresh = () => setIsFav(isFavorite(nowPlaying.title));
    window.addEventListener("rrz:favorites-changed", refresh);
    return () => window.removeEventListener("rrz:favorites-changed", refresh);
  }, [nowPlaying.title]);

  // Poll now-playing. CRITICAL: do NOT skip polling when the app is
  // backgrounded / phone in standby — otherwise CarPlay (which keeps the
  // audio alive but the UI hidden) never sees new titles and keeps showing
  // the song that was on-air when the screen was locked.
  useEffect(() => {
    let stop = false;
    const fetchNP = async () => {
      try {
        const r = await axios.get(`${API}/now-playing`, {
          timeout: 6000,
          params: { _: Date.now() },
        });
        if (!stop) setNowPlaying(r.data || {});
      } catch (e) {
        // silent
      }
    };
    fetchNP();
    // Faster cadence while playing so CarPlay metadata flips within ~10 s
    // of a song change. When paused and in background we throttle to 60 s.
    let currentDelay = 25000;
    let t = setInterval(fetchNP, currentDelay);
    const reschedule = () => {
      const want = isPlayingRef.current ? 10000 : (document.hidden ? 60000 : 25000);
      if (want === currentDelay) return;
      clearInterval(t);
      currentDelay = want;
      t = setInterval(fetchNP, currentDelay);
    };
    const onVis = () => { if (!document.hidden) fetchNP(); reschedule(); };
    document.addEventListener("visibilitychange", onVis);
    // Watch play state changes to retune the interval
    const retune = setInterval(reschedule, 2000);
    return () => {
      stop = true;
      clearInterval(t);
      clearInterval(retune);
      document.removeEventListener("visibilitychange", onVis);
    };
  }, []);

  // sync volume
  useEffect(() => {
    if (audioRef.current) {
      audioRef.current.volume = volume;
      audioRef.current.muted = muted;
    }
  }, [volume, muted]);

  // ==== Media Session API (populates CarPlay / lockscreen / notification with title, artist, cover) ====
  const lastMediaSongRef = useRef("");
  const artworkCacheRef = useRef(new Map()); // key: songKey → resolved artwork URL (or null if no iTunes match)
  const ARTWORK_CACHE_MAX = 50;
  useEffect(() => {
    if (typeof navigator === "undefined" || !("mediaSession" in navigator)) return;

    // Parse "Artist - Title" (best-effort, source varies)
    const raw = (nowPlaying.title || "").trim();
    let artist = "Radio Rumore Zero";
    let title  = raw || "60 · 70 · 80 · 90";
    if (raw.includes(" - ")) {
      const [left, ...rest] = raw.split(" - ");
      const right = rest.join(" - ").trim();
      if (left && right) {
        if (left.length <= right.length) { artist = left.trim(); title = right; }
        else                             { artist = right; title = left.trim(); }
      }
    }

    const setMeta = (artworkUrl) => {
      const artwork = artworkUrl
        ? [{ src: artworkUrl, sizes: "512x512", type: "image/jpeg" }]
        : [
            { src: "/icon-192.png", sizes: "192x192", type: "image/png" },
            { src: "/icon-512.png", sizes: "512x512", type: "image/png" },
          ];
      try {
        navigator.mediaSession.metadata = new window.MediaMetadata({
          title,
          artist,
          album: "Radio Rumore Zero",
          artwork,
        });
        // CarPlay workaround: iOS caches metadata per "track" and doesn't refresh
        // on continuous streams. Resetting positionState signals a new track so
        // CarPlay re-reads title/artist/artwork.
        if (typeof navigator.mediaSession.setPositionState === "function") {
          try {
            navigator.mediaSession.setPositionState({
              duration: 0,
              position: 0,
              playbackRate: 1,
            });
          } catch { /* ignore */ }
        }
      } catch (_) { /* ignore */ }
    };

    const cacheSet = (key, value) => {
      const m = artworkCacheRef.current;
      // Never overwrite a resolved URL with a null (miss) — prevents cache
      // poisoning if a duplicate iTunes lookup returns empty for a track
      // whose cover we already fetched successfully via prefetch.
      if (value === null && m.get(key)) return;
      // Refresh recency: delete then re-insert (Map preserves insertion order)
      if (m.has(key)) m.delete(key);
      m.set(key, value);
      // Evict oldest if over cap
      while (m.size > ARTWORK_CACHE_MAX) {
        const oldest = m.keys().next().value;
        m.delete(oldest);
      }
    };

    const songKey = raw.toLowerCase();
    const hasCached = songKey ? artworkCacheRef.current.has(songKey) : false;
    const cached = hasCached ? artworkCacheRef.current.get(songKey) : undefined;

    if (hasCached) {
      // Preserve resolved artwork (or known-no-match null) across polls / re-renders
      setMeta(cached);
      lastMediaSongRef.current = songKey;
      return;
    }

    // Unknown song → immediately set fallback icon, then look up iTunes async
    setMeta(null);

    if (songKey && songKey !== lastMediaSongRef.current) {
      lastMediaSongRef.current = songKey;
      // If the prefetch already started a lookup for this song, don't fire a duplicate.
      if (inFlightArtRef.current.has(songKey)) return;
      inFlightArtRef.current.add(songKey);
      const query = encodeURIComponent(raw);
      fetch(`https://itunes.apple.com/search?term=${query}&media=music&entity=song&limit=1`)
        .then((r) => r.ok ? r.json() : null)
        .then((j) => {
          const hit = j?.results?.[0];
          const url = hit?.artworkUrl100
            ? hit.artworkUrl100.replace("100x100", "512x512")
            : null;
          // Cache both hits and misses to avoid re-hitting the rate-limited iTunes API
          cacheSet(songKey, url);
          if (url) setMeta(url);
        })
        .catch(() => {})
        .finally(() => { inFlightArtRef.current.delete(songKey); });
    }
  }, [nowPlaying.title]);

  // Prefetch iTunes artwork for the next upcoming songs so it's ALREADY cached
  // when the track becomes now-playing. This lets us do a single MediaMetadata
  // assignment (with the cover) instead of the double-set pattern that
  // iOS CarPlay handles poorly (it can snapshot metadata once per song).
  const inFlightArtRef = useRef(new Set()); // song keys currently being fetched
  useEffect(() => {
    const onUpcoming = (ev) => {
      const songs = (ev?.detail?.songs || []).slice(0, 4);
      const cache = artworkCacheRef.current;
      const evictIfOver = () => {
        while (cache.size > ARTWORK_CACHE_MAX) {
          const oldest = cache.keys().next().value;
          cache.delete(oldest);
        }
      };
      for (const s of songs) {
        const raw = (s?.title || "").trim();
        if (!raw) continue;
        const key = raw.toLowerCase();
        // Skip if already cached (hit OR miss) or in flight
        if (cache.has(key) || inFlightArtRef.current.has(key)) continue;
        inFlightArtRef.current.add(key);
        fetch(`https://itunes.apple.com/search?term=${encodeURIComponent(raw)}&media=music&entity=song&limit=1`)
          .then((r) => r.ok ? r.json() : null)
          .then((j) => {
            const hit = j?.results?.[0];
            const url = hit?.artworkUrl100
              ? hit.artworkUrl100.replace("100x100", "512x512")
              : null;
            cache.set(key, url);
            evictIfOver();
            // If this song is currently on-air, apply the artwork right away
            // (the [nowPlaying.title] effect may have already short-circuited on miss).
            if (url && key === lastMediaSongRef.current && typeof navigator !== "undefined" && "mediaSession" in navigator) {
              try {
                const currentMeta = navigator.mediaSession.metadata;
                if (currentMeta) {
                  navigator.mediaSession.metadata = new window.MediaMetadata({
                    title: currentMeta.title,
                    artist: currentMeta.artist,
                    album: currentMeta.album,
                    artwork: [{ src: url, sizes: "512x512", type: "image/jpeg" }],
                  });
                }
              } catch { /* ignore */ }
            }
          })
          .catch(() => { /* keep uncached, will retry next event */ })
          .finally(() => { inFlightArtRef.current.delete(key); });
      }
    };
    window.addEventListener("rrz:upcoming", onUpcoming);
    return () => window.removeEventListener("rrz:upcoming", onUpcoming);
  }, []);

  // Media Session play/pause action handlers (so CarPlay buttons work)
  useEffect(() => {
    if (typeof navigator === "undefined" || !("mediaSession" in navigator)) return;
    const play = () => { if (!isPlaying) togglePlay(); };
    const pause = () => { if (isPlaying)  togglePlay(); };
    try {
      navigator.mediaSession.setActionHandler("play", play);
      navigator.mediaSession.setActionHandler("pause", pause);
      navigator.mediaSession.setActionHandler("stop", pause);
      navigator.mediaSession.playbackState = isPlaying ? "playing" : "paused";
    } catch (_) { /* ignore */ }
    return () => {
      try {
        navigator.mediaSession.setActionHandler("play", null);
        navigator.mediaSession.setActionHandler("pause", null);
        navigator.mediaSession.setActionHandler("stop", null);
      } catch (_) { /* ignore */ }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isPlaying]);

  const recordEvent = async (action) => {
    try {
      await axios.post(`${API}/listen-event`, {
        session_id: sessionIdRef.current,
        action,
      });
    } catch (e) { /* silent */ }
  };

  const togglePlay = async () => {
    const a = audioRef.current;
    if (!a) return;
    if (isPlaying) {
      a.pause();
      // cleanly disconnect from live stream (avoids spurious error event)
      a.removeAttribute("src");
      a.load();
      setIsPlaying(false);
      setIsLoading(false);
      recordEvent("stop");
      return;
    }
    setIsLoading(true);
    // Try direct HTTPS first (lowest latency), fallback to backend proxy on failure.
    const tryPlay = async (src) => {
      a.src = `${src}${src.includes("?") ? "&" : "?"}t=${Date.now()}`;
      await a.play();
    };
    try {
      await tryPlay(DIRECT_STREAM_URL);
      setIsPlaying(true);
      recordEvent("play");
    } catch (err1) {
      try {
        await tryPlay(PROXY_STREAM_URL);
        setIsPlaying(true);
        recordEvent("play");
      } catch (err2) {
        setIsLoading(false);
        setIsPlaying(false);
        toast.error("Impossibile avviare lo stream", {
          description: "Lo streamer potrebbe essere offline. Riprova tra poco.",
        });
      }
    }
  };

  // audio events
  useEffect(() => {
    const a = audioRef.current;
    if (!a) return;
    const onPlay    = () => { setIsPlaying(true); setIsLoading(false); };
    const onPause   = () => setIsPlaying(false);
    const onWait    = () => setIsLoading(true);
    const onPlaying = () => setIsLoading(false);
    const onError   = () => {
      setIsLoading(false);
      setIsPlaying(false);
      if (a.src) {
        toast.error("Stream non disponibile", {
          description: "Riprova tra qualche istante.",
        });
      }
    };
    a.addEventListener("play", onPlay);
    a.addEventListener("pause", onPause);
    a.addEventListener("waiting", onWait);
    a.addEventListener("playing", onPlaying);
    a.addEventListener("error", onError);

    // Listen for external pause requests (e.g. when user starts voice recording)
    const onExternalPause = () => {
      if (a && !a.paused) {
        a.pause();
        a.removeAttribute("src");
        a.load();
      }
      setIsPlaying(false);
      setIsLoading(false);
    };
    window.addEventListener("rrz:radio-pause-request", onExternalPause);

    return () => {
      a.removeEventListener("play", onPlay);
      a.removeEventListener("pause", onPause);
      a.removeEventListener("waiting", onWait);
      a.removeEventListener("playing", onPlaying);
      a.removeEventListener("error", onError);
      window.removeEventListener("rrz:radio-pause-request", onExternalPause);
    };
  }, []);

  const trackLine = nowPlaying.title
    ? `${nowPlaying.artist ? nowPlaying.artist + " — " : ""}${nowPlaying.title}`
    : "Radio Rumore Zero — i grandi successi 60·70·80·90";

  const onToggleFav = async () => {
    const raw = (nowPlaying.title || "").trim();
    if (!raw) {
      toast.info("Nessun brano in onda al momento");
      return;
    }
    // Removing? no lookup needed
    if (isFavorite(raw)) {
      toggleFavorite(raw);
      setIsFav(false);
      toast.success("Rimosso dai preferiti");
      return;
    }
    // Adding: try to find the *code from music-list so the future
    // "richiedi via WhatsApp" button can send it directly.
    let match = null;
    try { match = await lookupCode(raw); } catch { /* ignore */ }
    toggleFavorite(raw, match ? { code: match.code } : {});
    setIsFav(true);
    if (match) {
      toast.success(`Aggiunto ai preferiti ♥ (*${match.code})`);
    } else {
      toast.warning("Aggiunto ai preferiti", {
        description: "Non è nella lista richieste della radio — potresti non poterlo richiedere.",
      });
    }
  };

  const onShareTrack = async () => {
    const raw = (nowPlaying.title || "").trim();
    const url = (typeof window !== "undefined" && window.location?.origin) || "https://www.rumorezero.com";
    const text = raw
      ? `🎵 Sto ascoltando "${raw}" su Radio Rumore Zero — i grandi successi 60·70·80·90.`
      : "🎵 Sto ascoltando Radio Rumore Zero — i grandi successi 60·70·80·90.";

    // Open modal immediately with placeholder while the canvas renders
    setSharePreview({ open: true, blob: null, text, url });
    let blob = null;
    if (raw) {
      try {
        const cover = artworkCacheRef.current.get(raw.toLowerCase()) || null;
        blob = await generateShareCard({ raw, cover });
      } catch { /* ignore — modal will still allow text-only share */ }
    }
    setSharePreview({ open: true, blob, text, url });
  };

  // ==== Sleep timer: auto-pause the radio after N minutes ====
  useEffect(() => {
    if (!sleepUntil) { setSleepRemaining(0); return; }
    const tick = () => {
      const rem = Math.max(0, Math.round((sleepUntil - Date.now()) / 1000));
      setSleepRemaining(rem);
      if (rem <= 0) {
        setSleepUntil(null);
        window.dispatchEvent(new CustomEvent("rrz:radio-pause-request"));
        toast.info("Timer scaduto — radio in pausa 🌙");
      }
    };
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [sleepUntil]);

  // Close sleep menu when clicking outside
  useEffect(() => {
    if (!sleepMenuOpen) return;
    const onDoc = (e) => {
      if (!e.target.closest?.("[data-sleep-menu]")) setSleepMenuOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("touchstart", onDoc);
    return () => {
      document.removeEventListener("mousedown", onDoc);
      document.removeEventListener("touchstart", onDoc);
    };
  }, [sleepMenuOpen]);

  const setSleepMinutes = (min) => {
    setSleepMenuOpen(false);
    if (!min) {
      setSleepUntil(null);
      toast.info("Timer disattivato");
      return;
    }
    setSleepUntil(Date.now() + min * 60 * 1000);
    toast.success(`Timer impostato: ${min} min 🌙`);
  };

  const fmtSleep = (secs) => {
    const m = Math.floor(secs / 60);
    const s = secs % 60;
    return `${m}:${s.toString().padStart(2, "0")}`;
  };

  return (
    <div className="relative w-full max-w-2xl mx-auto">
      {/* ============== THE CASSETTE ============== */}
      <div
        data-testid="cassette-player"
        className="vintage-shell relative aspect-[5/3.2] sm:aspect-[5/3] rounded-[14px] sm:rounded-[18px] p-3 sm:p-5 overflow-hidden"
      >
        {/* 4 corner screws */}
        <span aria-hidden="true" className="vintage-screw absolute top-2 left-2 sm:top-3 sm:left-3 w-3 h-3 sm:w-4 sm:h-4 rounded-full" />
        <span aria-hidden="true" className="vintage-screw absolute top-2 right-2 sm:top-3 sm:right-3 w-3 h-3 sm:w-4 sm:h-4 rounded-full" />
        <span aria-hidden="true" className="vintage-screw absolute bottom-2 left-2 sm:bottom-3 sm:left-3 w-3 h-3 sm:w-4 sm:h-4 rounded-full" />
        <span aria-hidden="true" className="vintage-screw absolute bottom-2 right-2 sm:bottom-3 sm:right-3 w-3 h-3 sm:w-4 sm:h-4 rounded-full" />

        {/* Top brand strip (where 'maxell' is on the real cassette) */}
        <div className="relative px-6 sm:px-10 mb-2 sm:mb-3 flex items-center justify-between text-[#e8e6df]">
          <div className="flex items-center gap-2">
            <span
              data-testid="live-led"
              className={`inline-block w-2 h-2 rounded-full ${isPlaying ? "bg-[#FF6B00] led-on" : "bg-[#6a6660]"}`}
            />
            <span className="font-anton uppercase text-base sm:text-2xl tracking-[0.04em] text-[#1a1714] [text-shadow:0_1px_0_rgba(255,255,255,0.45)]">
              Rumore <span className="text-[#cf2020]">Zero</span>
            </span>
          </div>
          <span className="hidden sm:inline font-mono text-[10px] uppercase tracking-[0.18em] text-[#1a1714]/55">
            60·70·80·90
          </span>
        </div>

        {/* Main reel window with side labels */}
        <div className="flex items-stretch gap-2 sm:gap-3">
          {/* Left red label */}
          <div
            data-testid="left-side-label"
            className="vintage-red-label shrink-0 self-stretch flex flex-col items-center justify-center px-1.5 sm:px-2 py-2 rounded-sm w-[28px] sm:w-[40px]"
          >
            <span className="font-anton text-white text-2xl sm:text-4xl leading-none drop-shadow-[0_1px_0_rgba(0,0,0,0.5)]">
              90
            </span>
            <span className="font-mono text-[7px] sm:text-[9px] tracking-[0.18em] text-white/85 mt-1">
              MIN
            </span>
          </div>

          {/* Clear plastic window showing the reels */}
          <div className="vintage-window flex-1 relative rounded-md p-2 sm:p-4 flex items-center justify-between gap-1 sm:gap-2">
            <Reel playing={isPlaying} side="left" />

            {/* Tape strip stretched between reels */}
            <div className="relative flex-1 min-w-0 mx-1 h-1 sm:h-1.5 self-center bg-[#0a0907] rounded-sm overflow-hidden">
              <div className={`absolute inset-0 tape-strip ${isPlaying ? "flowing" : ""} opacity-90`} />
            </div>

            <Reel playing={isPlaying} side="right" />

            {/* Reflection highlights to sell the "clear plastic" look */}
            <div className="absolute inset-0 pointer-events-none rounded-md bg-gradient-to-br from-white/[0.04] via-transparent to-white/[0.02]" />
            <div className="absolute top-0 inset-x-0 h-1/2 pointer-events-none bg-gradient-to-b from-white/[0.06] to-transparent rounded-t-md" />
          </div>

          {/* Right red label */}
          <div
            data-testid="right-side-label"
            className="vintage-red-label shrink-0 self-stretch flex flex-col items-center justify-center px-1 sm:px-1.5 py-2 rounded-sm w-[28px] sm:w-[40px]"
          >
            <span className="font-anton text-white text-base sm:text-2xl leading-none">RZ</span>
            <span className="hidden sm:block font-mono text-[7px] sm:text-[8px] tracking-[0.15em] text-white/85 mt-1 text-center leading-tight">
              POSITION<br/>NORMAL<br/>TYPE I
            </span>
          </div>
        </div>

        {/* Bottom mechanism: felt pad + gear pin holes + tape exposure */}
        <div className="mt-2 sm:mt-3 mx-6 sm:mx-10 vintage-mechanism-slot rounded-sm h-7 sm:h-10 flex items-center justify-between px-3 sm:px-6">
          {/* left gear pin */}
          <span aria-hidden="true" className="w-2.5 h-2.5 sm:w-3 sm:h-3 rounded-full bg-[radial-gradient(circle,#3a3128_30%,#0a0907_70%)]" />
          {/* felt pad center */}
          <span aria-hidden="true" className="vintage-felt-pad flex-1 mx-2 sm:mx-3 h-3 sm:h-5 rounded-[2px]" />
          {/* right gear pin */}
          <span aria-hidden="true" className="w-2.5 h-2.5 sm:w-3 sm:h-3 rounded-full bg-[radial-gradient(circle,#3a3128_30%,#0a0907_70%)]" />
        </div>

        {/* Now-playing micro ticker (replaces classic paper label) */}
        <div className="relative mx-6 sm:mx-10 mt-2 sm:mt-3 overflow-hidden rounded-sm bg-[#1a1714] border border-[#3a3128]">
          <div data-testid="now-playing-ticker" className="marquee-track inline-flex whitespace-nowrap py-1 font-mono text-[10px] sm:text-xs text-[#FF6B00]">
            <span className="px-6">► {trackLine}</span>
            <span className="px-6">► {trackLine}</span>
            <span className="px-6">► {trackLine}</span>
            <span className="px-6">► {trackLine}</span>
          </div>
        </div>
      </div>

      {/* ============== CONTROLS (below the cassette, brand colours) ============== */}
      <div className="mt-5 sm:mt-6 flex flex-row items-center gap-4 sm:gap-6">
        <button
          data-testid="play-button"
          onClick={togglePlay}
          aria-label={isPlaying ? "Pausa" : "Riproduci"}
          className="group relative shrink-0 w-16 h-16 sm:w-20 sm:h-20 rounded-full bg-[#FF6B00] text-[#12100e] flex items-center justify-center hover:bg-[#ff8226] active:scale-95 transition-all duration-200 shadow-[0_0_24px_rgba(255,107,0,0.5)]"
        >
          {isLoading ? (
            <Loader2 size={28} className="animate-spin sm:hidden" />
          ) : isPlaying ? (
            <Pause size={28} fill="currentColor" className="sm:hidden" />
          ) : (
            <Play size={28} fill="currentColor" className="ml-0.5 sm:hidden" />
          )}
          {isLoading ? (
            <Loader2 size={32} className="animate-spin hidden sm:block" />
          ) : isPlaying ? (
            <Pause size={32} fill="currentColor" className="hidden sm:block" />
          ) : (
            <Play size={32} fill="currentColor" className="ml-1 hidden sm:block" />
          )}
          <span className="absolute inset-0 rounded-full border-2 border-[#FF6B00]/40 group-hover:border-[#FF6B00] transition-colors" />
        </button>

        <div className="flex-1 min-w-0 flex items-center gap-2 sm:gap-3">
          <button
            data-testid="mute-button"
            onClick={() => setMuted((m) => !m)}
            className="shrink-0 p-2 rounded-md border border-[#3a3128] text-[#E1A140] hover:bg-[#1f1a16] transition"
            aria-label={muted ? "Riattiva audio" : "Silenzia"}
          >
            {muted || volume === 0 ? <VolumeX size={18} /> : <Volume2 size={18} />}
          </button>
          <input
            data-testid="volume-slider"
            type="range"
            min={0}
            max={1}
            step={0.01}
            value={muted ? 0 : volume}
            onChange={(e) => { setMuted(false); setVolume(parseFloat(e.target.value)); }}
            className="flex-1 min-w-0 accent-[#FF6B00] cursor-pointer"
            aria-label="Volume"
          />
          <span className="shrink-0 font-mono text-xs text-[#A39A8F] w-8 sm:w-10 text-right tabular-nums">
            {Math.round((muted ? 0 : volume) * 100)}
          </span>
        </div>
      </div>

      {/* Equalizer + status */}
      <div className="mt-4 flex items-center justify-between gap-4 px-1">
        <Equalizer active={isPlaying} />
        <div className="flex flex-col items-end gap-0.5">
          <span data-testid="status-text" className="font-mono text-[10px] sm:text-[11px] uppercase tracking-widest text-[#A39A8F]">
            {isLoading ? "Connessione…" : isPlaying ? "In riproduzione" : "In pausa"}
          </span>
          {nowPlaying.listeners != null && (
            <span
              data-testid="listeners-count"
              className="inline-flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-widest text-[#FF6B00]"
            >
              <span className="relative flex w-1.5 h-1.5">
                <span className="absolute inline-flex w-full h-full rounded-full bg-[#FF6B00] opacity-70 animate-ping" />
                <span className="relative inline-flex w-1.5 h-1.5 rounded-full bg-[#FF6B00]" />
              </span>
              {nowPlaying.listeners} in ascolto ora
            </span>
          )}
        </div>
      </div>

      {/* Track actions: heart + share + sleep timer */}
      <div
        data-testid="track-actions"
        className="mt-3 flex items-center justify-end gap-2 flex-wrap"
      >
        <button
          type="button"
          data-testid="favorite-toggle"
          onClick={onToggleFav}
          aria-pressed={isFav}
          aria-label={isFav ? "Rimuovi dai preferiti" : "Aggiungi ai preferiti"}
          className={`inline-flex items-center gap-2 px-3 py-2 rounded-lg border transition-all duration-200 font-mono text-[10px] uppercase tracking-[0.18em] ${
            isFav
              ? "bg-[#FF6B00]/10 border-[#FF6B00]/60 text-[#FF6B00]"
              : "bg-[#1a1816] border-[#332f2b] text-[#A39A8F] hover:text-[#FF6B00] hover:border-[#FF6B00]/50"
          }`}
        >
          <Heart size={14} fill={isFav ? "currentColor" : "none"} />
          {isFav ? "Nei preferiti" : "Preferito"}
        </button>
        <button
          type="button"
          data-testid="share-track"
          onClick={onShareTrack}
          aria-label="Condividi il brano in onda"
          className="inline-flex items-center gap-2 px-3 py-2 rounded-lg border bg-[#1a1816] border-[#332f2b] text-[#A39A8F] hover:text-[#1E88E5] hover:border-[#1E88E5]/50 font-mono text-[10px] uppercase tracking-[0.18em] transition-all duration-200"
        >
          <Share2 size={14} />
          Condividi
        </button>

        {/* Sleep timer dropdown */}
        <div className="relative" data-sleep-menu>
          <button
            type="button"
            data-testid="sleep-timer-button"
            onClick={() => setSleepMenuOpen((v) => !v)}
            aria-haspopup="menu"
            aria-expanded={sleepMenuOpen}
            aria-label="Timer sleep"
            className={`inline-flex items-center gap-2 px-3 py-2 rounded-lg border transition-all duration-200 font-mono text-[10px] uppercase tracking-[0.18em] ${
              sleepUntil
                ? "bg-[#7c3aed]/15 border-[#a78bfa]/50 text-[#c4b5fd]"
                : "bg-[#1a1816] border-[#332f2b] text-[#A39A8F] hover:text-[#c4b5fd] hover:border-[#a78bfa]/50"
            }`}
          >
            <Moon size={14} />
            {sleepUntil ? (
              <span data-testid="sleep-timer-countdown" className="tabular-nums">
                {fmtSleep(sleepRemaining)}
              </span>
            ) : (
              <span>Timer</span>
            )}
          </button>
          {sleepMenuOpen && (
            <div
              role="menu"
              data-testid="sleep-timer-menu"
              className="absolute right-0 top-full mt-1 min-w-[160px] rounded-lg border border-[#332f2b] bg-[#12100e] shadow-[0_10px_30px_rgba(0,0,0,0.5)] overflow-hidden z-20"
            >
              {[15, 30, 45, 60].map((min) => (
                <button
                  key={min}
                  type="button"
                  role="menuitem"
                  data-testid={`sleep-option-${min}`}
                  onClick={() => setSleepMinutes(min)}
                  className="w-full px-3 py-2 text-left font-mono text-[11px] uppercase tracking-[0.18em] text-[#F5F2EB] hover:bg-[#1a1816] hover:text-[#c4b5fd] transition"
                >
                  {min} min
                </button>
              ))}
              {sleepUntil && (
                <button
                  type="button"
                  role="menuitem"
                  data-testid="sleep-option-off"
                  onClick={() => setSleepMinutes(null)}
                  className="w-full px-3 py-2 text-left font-mono text-[11px] uppercase tracking-[0.18em] text-[#ff6b6b] hover:bg-[#1a1816] transition border-t border-[#332f2b]"
                >
                  Disattiva
                </button>
              )}
            </div>
          )}
        </div>
      </div>

      <SharePreviewModal
        open={sharePreview.open}
        blob={sharePreview.blob}
        text={sharePreview.text}
        url={sharePreview.url}
        onClose={() => setSharePreview((s) => ({ ...s, open: false }))}
      />

      {/* hidden audio element */}
      <audio
        ref={audioRef}
        data-testid="audio-element"
        preload="none"
      />
    </div>
  );
}
